import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Eye, CheckCircle, XCircle, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const { verifyEmail, resendVerification } = useAuth();
  const navigate = useNavigate();

  const handleVerification = useCallback(async () => {
    setLoading(true);
    const result = await verifyEmail(token);
    
    if (result.success) {
      setVerified(true);
      toast.success(result.message);
    } else {
      setError(result.error);
      toast.error(result.error);
    }
    
    setLoading(false);
  }, [token, verifyEmail]);

  useEffect(() => {
    if (token) {
      handleVerification();
    } else {
      setLoading(false);
      setError('No verification token provided');
    }
  }, [token, handleVerification]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResending(true);
    
    const result = await resendVerification(resendEmail);
    
    if (result.success) {
      toast.success(result.message);
    } else {
      toast.error(result.error);
    }
    
    setResending(false);
  };

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
        style={{ backgroundImage: `url('https://images.pexels.com/photos/17796162/pexels-photo-17796162.jpeg?auto=compress&cs=tinysrgb&w=1920')` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        
        <Card className="relative z-10 w-full max-w-md p-8 mx-4 text-center">
          <Loader2 className="h-16 w-16 text-[#E31837] mx-auto mb-4 animate-spin" />
          <h1 className="text-2xl font-bold text-[#333333] mb-2">Verifying Email...</h1>
          <p className="text-gray-600">Please wait while we verify your email address.</p>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{ backgroundImage: `url('https://images.pexels.com/photos/17796162/pexels-photo-17796162.jpeg?auto=compress&cs=tinysrgb&w=1920')` }}
    >
      <div className="absolute inset-0 bg-black/60"></div>
      
      <Card data-testid="verify-email-card" className="relative z-10 w-full max-w-md p-8 mx-4">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Eye className="h-12 w-12 text-[#E31837]" />
          </div>
          <h1 className="text-3xl font-bold text-[#333333] mb-2">
            {verified ? 'Email Verified!' : 'Verification Failed'}
          </h1>
        </div>

        {verified ? (
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <p className="text-gray-600">
              Your email has been successfully verified. You can now log in to your account.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-[#E31837] hover:bg-[#C4122C] text-white"
              data-testid="go-to-login-button"
            >
              Go to Login
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <XCircle className="h-16 w-16 text-red-500" />
              </div>
              <p className="text-gray-600 mb-4">{error}</p>
            </div>
            
            <div className="border-t pt-6">
              <p className="text-center text-gray-600 mb-4">
                Need a new verification link?
              </p>
              <form onSubmit={handleResendVerification} className="space-y-4">
                <div>
                  <Label htmlFor="resendEmail">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="resendEmail"
                      data-testid="resend-email-input"
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="pl-10"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#E31837] hover:bg-[#C4122C] text-white"
                  disabled={resending}
                  data-testid="resend-verification-button"
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </Button>
              </form>
            </div>

            <div className="text-center pt-4">
              <Link 
                to="/login" 
                className="text-[#E31837] hover:underline"
                data-testid="back-to-login-link"
              >
                Back to Login
              </Link>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
